import React from "react";

// Page chrome for stepper-driven dashboard form pages, modeled exactly on
// CreateMember.tsx's structure (sticky stepper above an aria-labelled <main>
// with an sr-only heading, alerts, content stack, and a bottom action band).
//
// Phase B: CreateMember is now a consumer (B2 rewire — its emitted chrome is
// byte-equal to this shell's). UpdateMember remains hand-rolled (it uses an
// unclamped w-full layout with an in-main StepFooter) pending its own
// DOM-diff before adoption.

export const formPageMaxWidthClassName = "mx-auto w-full max-w-[1180px]";

export type FormPageShellProps = {
  /** id for the sr-only <h2>, e.g. `create-member-step-${step}`. */
  headingId: string;
  /** Screen-reader-only page heading text. */
  heading: string;
  /** Step content, wrapped in contentClassName. */
  children: React.ReactNode;
  /** e.g. <MemberStepProgress />, rendered before <main>. */
  stepper?: React.ReactNode;
  /** e.g. <UnsavedChangesPrompt />, rendered before the stepper. */
  beforeMain?: React.ReactNode;
  /** Status alerts rendered between the sr-only heading and the content. */
  alerts?: React.ReactNode;
  /** Bottom bar button row content. */
  actionBar?: React.ReactNode;
  /** Footer alert rendered above the action bar button row. */
  actionBarAlert?: React.ReactNode;
  /** Ref to the root wrapper div (CreateMember's scroll-to-top anchor). */
  rootRef?: React.Ref<HTMLDivElement>;
  contentClassName?: string;
  /** Top padding for <main>. Defaults to the existing shared 24 px. */
  mainPaddingTopClassName?: string;
  /**
   * Width/layout classes for <main>. Defaults to the shared 1180px clamp so
   * existing consumers (CreateMember) emit byte-identical chrome (B5/Q-034).
   */
  mainClassName?: string;
  /** Width and padding for the action row. Defaults to the shared form width. */
  actionBarClassName?: string;
};

export const FormPageShell = ({
  headingId,
  heading,
  children,
  stepper,
  beforeMain,
  alerts,
  actionBar,
  actionBarAlert,
  rootRef,
  contentClassName = "space-y-6",
  mainClassName = formPageMaxWidthClassName,
  mainPaddingTopClassName = "pt-6",
  actionBarClassName = `${formPageMaxWidthClassName} px-2`,
}: FormPageShellProps) => (
  <div ref={rootRef} className="min-h-full bg-white px-0 pb-36">
    {beforeMain}
    {stepper}
    <main
      aria-labelledby={headingId}
      className={`${mainClassName} ${mainPaddingTopClassName}`}
    >
      <h2 id={headingId} className="sr-only">
        {heading}
      </h2>
      {alerts}
      <div className={contentClassName}>{children}</div>
    </main>
    {(actionBar || actionBarAlert) && (
      <div className="mt-8 border-t border-gray-200 bg-white py-4">
        <div className={actionBarClassName}>
          {actionBarAlert}
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            {actionBar}
          </div>
        </div>
      </div>
    )}
  </div>
);
